'use client';
import React, {useEffect, useRef} from 'react';

import {HoverCard, HoverCardContent, HoverCardTrigger} from '@/components/ui/hover-card';
import {Badge} from '@/components/ui/badge';
import {Button} from '@/components/ui/button';
import {BadgeMinus, BadgePlus, CheckCircle} from 'lucide-react';
import {eventTicketForCurrentEvent} from "@/app/events/[id]/store/buy-tickets.store";
import {DrawingItemsTypes, ICommonSeatItemProps, IDrawingItem, ISeatItemProps} from "@/app/dashboard/models/Seating";
import {cn} from "@/lib/utils";
import {bookedSeats, deSelectSeatItem, selectSeatItem} from "@/app/events/[id]/store/buy-ticket-seating.store";
import {useSignals} from "@preact/signals-react/runtime";


const CommonSeatItem = (
  props: IDrawingItem<ISeatItemProps> & ICommonSeatItemProps
) => {
  const { setSeatRef, seat, colIndex, ticketCategory, isBooked = false } = props;
  useSignals()

  const scaleY = (1 / (props.scaleY ?? 0)).toFixed(3);
  const scaleX = (1 / (props.scaleX ?? 0)).toFixed(3);
  return (
    <HoverCard  >
      <HoverCardTrigger
        style={{
          transform: `translate(0)`
        }}

      >
        <div
            onClick={()=>{
                if(isBooked) {
                    return;
                }
                if (seat.isSelected) {
                    deSelectSeatItem(seat);
                } else {
                    selectSeatItem(seat);
                }
            }}
          ref={setSeatRef}
          key={colIndex}
          className={
          cn('flex h-8 w-8 cursor-pointer items-center justify-center rounded-full border  transition-colors duration-200 ', seat.isSelected ? "bg-slate-500 hover:bg-slate-400 text-white":"bg-gray-300 hover:bg-gray-400 ", isBooked ? "bg-stone-600 hover:bg-stone-800 text-white":"bg-gray-300 hover:bg-gray-400 ")}

        >
          {seat?.seatNumber ?? ''}
        </div>
      </HoverCardTrigger>

      <HoverCardContent
        className="parent"
        style={{
          transform: `scale(${scaleX}, ${scaleY}) rotate(${
            360 - props.rotation
          }deg)`,
          transformOrigin: 'center'
        }}
      >
        <div className=" flex flex-col gap-3 " >
          <div className="flex gap-2 font-bold">
            Seat number
            <Badge>{seat?.seatNumber ?? 'Seat'}</Badge>
          </div>
            {
            isBooked ? (
                <div className="text-red-500 text-sm">This seat is booked</div>
            ) : null
            }
          {!isBooked && (ticketCategory ? (
            <>
              <div className="flex gap-2 text-sm">
                {ticketCategory.title}
                <strong>
                  {ticketCategory.price} {ticketCategory.currencyFormat}
                </strong>

                { seat.isSelected ?
                    (<div className="text-green-900 flex gap-1"><CheckCircle size={15}/>Selected</div>) : null
                }
              </div>
              {
                seat.isSelected ? (
                    <Button className="flex gap-3"  variant="outline"   onClick={()=> deSelectSeatItem(seat)} >
                      <BadgeMinus size={20} /> Remove
                    </Button>
                    ) : (
                    <Button className="flex gap-3"     onClick={()=> selectSeatItem(seat)}>
                      <BadgePlus size={20} /> Select
                    </Button>
                )
              }
            </>
          ) : (
            <div className="text-sm text-red-500">
              No ticket category assigned
            </div>
          ))}
          <div className="text-xs text-gray-400"></div>
        </div>
      </HoverCardContent>
    </HoverCard>
  );
};

export const TicketBuyerSeatGroup = (props: IDrawingItem<ISeatItemProps>) => {
  const { rows, columns, curveFactor } = props.data ?? {
    rows: 5,
    columns: 5,
    curveFactor: 0
  };

  useSignals()

    const bookedSeatsList = bookedSeats.value;


  const seatRefs = useRef<Array<Array<HTMLDivElement | null>>>(
    Array.from({ length: rows }, () => Array(columns).fill(null))
  );
  useEffect(() => {
    const seatsXY: { x: number; y: number }[] = [];
    seatRefs.current.forEach((row, rowIndex) => {
      row.forEach((seat, colIndex) => {
        if (seat) {
          const rect = seat.getBoundingClientRect();
          const offsetX = rect.left - props.x;
          const offsetY = rect.top - props.y;

          seatsXY.push({
            x: offsetX,
            y: offsetY
          });
        }
      });
    });
  }, [rows, columns]);
  return (
    <div className="absolute flex flex-col items-center gap-2">
      {Array.from({ length: rows }).map((_, rowIndex) => {
        const curveOffset = (rowIndex - rows / 2) ** 2 * 0.5 * curveFactor;
        return (
          <div
            key={rowIndex}
            className="flex justify-center gap-2"
            style={{
              marginLeft: curveOffset
            }}
          >
            {Array.from({ length: columns }).map((_, colIndex) => {
              const seat = (props.data?.seats ?? [])[
                rowIndex * columns + colIndex
              ];

              const ticketCategory = eventTicketForCurrentEvent.value?.find(
                (tc) => tc.id === seat?.ticketCategoryId
              );

              const isBooked = bookedSeatsList.some(bs => bs.seatId === seat?.seatNumber);

              return (
                <CommonSeatItem
                  setSeatRef={(el) => {
                    seatRefs.current[rowIndex][colIndex] = el;
                  }}
                  colIndex={colIndex}
                  seat={seat}
                  ticketCategory={ticketCategory}
                  isBooked={isBooked}
                  key={colIndex}
                  {...props}
                />
              );
            })}
          </div>
        );
      })}
    </div>
  );
};
export const ticketBuyerStaticItemMap = {
  [DrawingItemsTypes.SEAT_GROUP]: TicketBuyerSeatGroup
};
